"use client";
import { useEffect, useState } from "react";
import BasicDetails from "./BasicDetails";
import OnboardingLayout from "./OnboardingLayout";
import ProjectDocuments from "./ProjectDocuments";
import VolumneForm from "./VolumneForm";
import { SuccessModal } from "../../Modal/successModal";
import CompletedImg from "./CompletedImg";
import { ApplicationForm } from "./ApplicationForm";
import { HelpModal } from "./HelpModal";

const stepsDummy = [
  {
    label: "Basic Project Details",
    status: "IN_PROGRESS",
    subText: "Core details about the project",
    name: "basicProjectDetails",
  },
  {
    label: "Project Documents",
    status: "NOT_STARTED",
    subText: "Project document information",
    name: "documents",
  },
  {
    label: "Application Form",
    status: "NOT_STARTED",
    subText: "Questionnaire for the project",
    name: "applicationForm",
  },
  {
    label: "Project Economics & Deliverables",
    status: "NOT_STARTED",
    subText: "Information on financing type",
    name: "volumePricing",
  },
];

const stepsClick = {
  step1: false,
  step2: false,
  step3: false,
  step4: false,
};

export function OnboardingPage({
  fileTypeImages,
  assets,
  DynamicImage,
  RichText,
  toast,
  useMutation,
  useQueryClient,
  useQueryData,
  useSession,
  getDirtyValues,
  mutateAction,
  getFileTypeImage,
  onClose,
  variables,
  timeZoneDateTimeUser,
  projectId: editId,
  useRouter,
  projectData,
  projectLoading,
}: any) {
  const [steps, setSteps] = useState(stepsDummy);
  const [selectedTab, setSelectedTab] = useState(0);
  const [submitCurrentStep, setSubmitCurrentStep] = useState(stepsClick);
  const [discardStep, setDiscardStep] = useState(stepsClick);
  const [nextClicked, setNextClicked] = useState(stepsClick);
  const [prevClicked, setPrevClicked] = useState(stepsClick);
  const [disableBtn, setDisableBtn] = useState({
    prev: false,
    next: false,
    discard: false,
    save: false,
    submit: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [stepsUpdated, setStepsUpdated] = useState(false);
  const [helpModalOpen, setHelpModal] = useState(false);
  const [checkSaveState, setCheckSaveState] = useState<any>(null);
  const [projectDetails, setProjectDetails] = useState<any>(null);
  const [submitModal, setSubmitModal] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const handleCloseSubmitModal = () => setSubmitModal(false);

  // const { data, isFetching } = useQueryData({
  //   queryKey: ["get-project", editId],
  //   endPoint: `/project/v2/onboarding/${editId}`,
  //   enabled: !!editId,
  // });

  const { refetch: applicationRefetch, isFetching: applicationFormLoading } =
    useQueryData({
      queryKey: [
        "questionare",
        "APPLICATION_FORM",
        editId || projectDetails?.id,
      ],
      endPoint: `/project/v2/onboarding/form/metadata?projectId=${
        editId || projectDetails?.id
      }&type=APPLICATION_FORM`,
      enabled: !!editId || !!projectDetails?.id,
    });

  const { refetch: financingRefetch, isFetching: financeFormLoading } =
    useQueryData({
      queryKey: ["questionare", "FINANCING", editId || projectDetails?.id],
      endPoint: `/project/v2/onboarding/form/metadata?projectId=${
        editId || projectDetails?.id
      }&type=FINANCING`,
      enabled: !!editId || !!projectDetails?.id,
    });

  useEffect(() => {
    if (projectData) setProjectDetails(projectData?.project);
  }, [projectData]);

  useEffect(() => {
    if (projectDetails) {
      const newSteps = steps?.map((item, index) => ({
        ...item,
        status:
          projectDetails?.stepper?.[item?.name] !== "COMPLETED" &&
          selectedTab === index
            ? "IN_PROGRESS"
            : projectDetails?.stepper?.[item?.name],
      }));
      const allStepsCompleted = !newSteps?.some(
        (item) => item.status !== "COMPLETED"
      );
      if (allStepsCompleted) setDisableBtn({ ...disableBtn, submit: false });
      else setDisableBtn({ ...disableBtn, submit: true });
      setSteps(newSteps);
      setStepsUpdated(true);
    }
  }, [projectDetails, selectedTab]);

  useEffect(() => {
    if (stepsUpdated) {
      const completedStep = steps?.length
        ? steps.length -
          1 -
          steps
            .slice()
            .reverse()
            .findIndex((item) => item?.status === "COMPLETED")
        : -1;
      if (completedStep === 4) setSelectedTab(0);
      else if (completedStep !== 3) setSelectedTab(completedStep + 1);
      else if (completedStep === 3) setSelectedTab(3);
      else setSelectedTab(0);
    }
  }, [stepsUpdated]);

  const nextOnClickHandler = () => {
    setNextClicked({
      step1: selectedTab === 0,
      step2: selectedTab === 1,
      step3: selectedTab === 2,
      step4: selectedTab === 3,
    });
  };
  const prevOnClickHandler = () => {
    setPrevClicked({
      step1: selectedTab === 0,
      step2: selectedTab === 1,
      step3: selectedTab === 2,
      step4: selectedTab === 3,
    });
  };

  const saveOnClickHandler = () => {
    setSubmitCurrentStep({
      step1: selectedTab === 0,
      step2: selectedTab === 1,
      step3: selectedTab === 2,
      step4: selectedTab === 3,
    });
  };

  const submitOnClickHandler = () => {
    setSubmitClicked(true);
  };

  const discardOnClickHandler = () => {
    setDiscardStep({
      step1: selectedTab === 0,
      step2: selectedTab === 1,
      step3: selectedTab === 2,
      step4: selectedTab === 3,
    });
  };

  const tabSwitch = (ind: number) => {
    setCheckSaveState({ current: selectedTab, destination: ind });
    if (selectedTab !== 2 && selectedTab !== 3) {
      if (projectDetails) setSelectedTab(ind);
    }
  };

  return (
    <>
      <OnboardingLayout
        isPending={projectLoading}
        steps={steps}
        isLoading={isLoading}
        disableBtn={disableBtn}
        BasicDetails={
          <BasicDetails
            assets={assets}
            RichText={RichText}
            setSelectedTab={setSelectedTab}
            DynamicImage={DynamicImage}
            submitCurrentStep={submitCurrentStep}
            setSubmitCurrentStep={setSubmitCurrentStep}
            toast={toast}
            useMutation={useMutation}
            useQueryClient={useQueryClient}
            useSession={useSession}
            getDirtyValues={getDirtyValues}
            mutateAction={mutateAction}
            variables={variables}
            setIsLoading={setIsLoading}
            setProjectDetails={setProjectDetails}
            projectDetails={projectDetails}
          />
        }
        ApplicationForm={
          <ApplicationForm
            RichText={RichText}
            assets={assets}
            variables={variables}
            DynamicImage={DynamicImage}
            getFileTypeImage={getFileTypeImage}
            useSession={useSession}
            useMutation={useMutation}
            useQueryData={useQueryData}
            useQueryClient={useQueryClient}
            mutateAction={mutateAction}
            toast={toast}
            submitCurrentStep={submitCurrentStep}
            setSubmitCurrentStep={setSubmitCurrentStep}
            discardStep={discardStep}
            setDiscardStep={setDiscardStep}
            projectId={editId || projectDetails?.id}
            setDisableBtn={setDisableBtn}
            setIsLoading={setIsLoading}
            timeZoneDateTimeUser={timeZoneDateTimeUser}
            nextClicked={nextClicked}
            setNextClicked={setNextClicked}
            prevClicked={prevClicked}
            setPrevClicked={setPrevClicked}
            setSelectedTab={setSelectedTab}
            selectedTab={selectedTab}
            projectDetails={projectDetails}
            setProjectDetails={setProjectDetails}
            checkSaveState={checkSaveState}
            setCheckSaveState={setCheckSaveState}
            applicationFormLoading={applicationFormLoading}
          />
        }
        Documents={
          <ProjectDocuments
            fileTypeImages={fileTypeImages}
            setSelectedTab={setSelectedTab}
            setIsLoading={setIsLoading}
            variables={variables}
            projectDetails={projectDetails}
            assets={assets}
            DynamicImage={DynamicImage}
            submitCurrentStep={submitCurrentStep}
            setSubmitCurrentStep={setSubmitCurrentStep}
            toast={toast}
            setProjectDetails={setProjectDetails}
            useMutation={useMutation}
            useQueryClient={useQueryClient}
            useSession={useSession}
            getDirtyValues={getDirtyValues}
            mutateAction={mutateAction}
            applicationRefetch={applicationRefetch}
            financingRefetch={financingRefetch}
          />
        }
        Volume={
          <VolumneForm
            RichText={RichText}
            assets={assets}
            DynamicImage={DynamicImage}
            getFileTypeImage={getFileTypeImage}
            useSession={useSession}
            useMutation={useMutation}
            useQueryData={useQueryData}
            mutateAction={mutateAction}
            toast={toast}
            submitCurrentStep={submitCurrentStep}
            setSubmitCurrentStep={setSubmitCurrentStep}
            discardStep={discardStep}
            useQueryClient={useQueryClient}
            setDiscardStep={setDiscardStep}
            projectId={editId || projectDetails?.id}
            setDisableBtn={setDisableBtn}
            setIsLoading={setIsLoading}
            timeZoneDateTimeUser={timeZoneDateTimeUser}
            nextClicked={nextClicked}
            setNextClicked={setNextClicked}
            prevClicked={prevClicked}
            setPrevClicked={setPrevClicked}
            setSelectedTab={setSelectedTab}
            projectDetails={projectDetails}
            setProjectDetails={setProjectDetails}
            checkSaveState={checkSaveState}
            variables={variables}
            setCheckSaveState={setCheckSaveState}
            submitVariables={{
              setSubmitModal,
              submitClicked,
              setSubmitClicked,
              setIsSubmitLoading,
              isSubmitLoading,
            }}
            financeFormLoading={financeFormLoading}
          />
        }
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        discardOnClick={discardOnClickHandler}
        nextOnClickHandler={nextOnClickHandler}
        prevOnClickHandler={prevOnClickHandler}
        saveOnCLick={saveOnClickHandler}
        submitOnClick={submitOnClickHandler}
        onClose={onClose}
        isSubmitLoading={isSubmitLoading}
        tabSwitch={tabSwitch}
        openHelpModal={() => setHelpModal(true)}
        variables={variables}
      />
      <SuccessModal
        width="436px"
        open={submitModal}
        title="You have successfully completed the initial steps to onboard your project!"
        desc=" The next step is to provide details for sections like stakeholders, financials, and the carbon schedule. These sections can be edited as needed, ensuring that the information on the platform remains current."
        containedOnClick={() => {
          onClose();
          handleCloseSubmitModal();
          variables.projectRefetch();
        }}
        containedButtonTitle="View Project"
        iconSvg={<CompletedImg modalTick />}
        styleColor="25855A"
      />
      <HelpModal
        open={helpModalOpen}
        handleClose={() => setHelpModal(false)}
        props={{
          useMutation,
          mutateAction,
          useSession,
          toast,
          projectId: editId || projectDetails?.id,
        }}
      />
    </>
  );
}
